import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);


    // pharmacist form
    if (this.dataset.pharmacistFormEnabled === 'true') {
      this.addEventListener('change', this.onPharmacistFieldChange.bind(this));
      this.populatePharmacistForms();

      const cartForm = this.querySelector('#cart-form');
      cartForm?.addEventListener('submit', this.onCartFormSubmit.bind(this));

      this.updateCheckoutAvailability();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        // pharmacist form
        if (this.dataset.pharmacistFormEnabled === 'true') {
          this.populatePharmacistForms();
          this.updateCheckoutAvailability();
        }

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // pharmacist form
      if (this.dataset.pharmacistFormEnabled === 'true') {
        this.populatePharmacistForms();
        this.updateCheckoutAvailability();
      }

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }



  /**
   * For Pharmacist form
   */

  populatePharmacistForms() {
    this.querySelectorAll('[data-patient-info]').forEach((wrapper) => {
      const raw = wrapper.dataset.patientInfo;
      if (!raw) return;

      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        return;
      }

      Object.entries(data).forEach(([key, value]) => {
        const fields = wrapper.querySelectorAll(`[data-property-name="${CSS.escape(key)}"]`);
        if (!fields.length) return;

        fields.forEach((field) => {
          if (field.type === 'radio') {
            field.checked = field.value === value;
          } else if (field.type === 'checkbox') {
            field.checked = !!value;
          } else {
            field.value = value;
          }
        });
      });
    });
  }

  onPharmacistFieldChange(event) {
    const field = event.target;
    const row = field.closest('.cart-items__form-row');
    if (!row) return;

    this.validatePharmacistField(field);
    this.savePharmacistLine(row);
    this.updateCheckoutAvailability();
  }

  validatePharmacistField(field) {
    if (field.type === 'radio') {
      const fieldset = field.closest('fieldset');
      if (!fieldset) return;
      const group = fieldset.querySelectorAll(`input[name="${field.name}"]`);
      const anyChecked = Array.from(group).some((r) => r.checked);
      fieldset.classList.toggle('cart-items__question--invalid', !anyChecked);
    } else {
      field.classList.toggle('cart-items__field--invalid', !field.checkValidity());
    }
  }

  /**
   * Validates all pharmacist-form fields within a row.
   * @param {HTMLElement} row
   * @param {Object} [options]
   * @param {boolean} [options.markInvalid] - Whether to toggle invalid CSS classes.
   * @returns {boolean} Whether the row is fully valid/complete.
   */
  validateRow(row, { markInvalid = false } = {}) {
    const fields = row.querySelectorAll('[data-property-name]');
    let allValid = true;

    fields.forEach((field) => {
      if (field.type === 'radio') return;
      const valid = field.checkValidity();
      if (markInvalid) field.classList.toggle('cart-items__field--invalid', !valid);
      if (!valid) allValid = false;
    });

    row.querySelectorAll('fieldset').forEach((fieldset) => {
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      if (!radios.length) return;
      const anyChecked = Array.from(radios).some((r) => r.checked);
      if (markInvalid) fieldset.classList.toggle('cart-items__question--invalid', !anyChecked);
      if (!anyChecked) allValid = false;
    });

    return allValid;
  }

  updateCheckoutAvailability() {
    const rows = this.querySelectorAll('.cart-items__form-row');
    let allValid = true;

    rows.forEach((row) => {
      if (!this.validateRow(row)) allValid = false;
    });

    this.setCheckoutDisabled(!allValid);
  }

  setCheckoutDisabled(disabled) {
    const checkoutButton = document.getElementById('checkout');
    if (checkoutButton) {
      checkoutButton.disabled = disabled;
      checkoutButton.classList.toggle('cart__checkout-button--disabled', disabled);
    }

    const dynamicCheckoutWrapper = document.getElementById('dynamic-checkout-cart');
    if (dynamicCheckoutWrapper) {
      dynamicCheckoutWrapper.style.display = disabled ? 'none' : '';
    }

    this.togglePharmacistFormNotice(disabled);
  }
  
  togglePharmacistFormNotice(show) {
    let notice = document.getElementById('pharmacist-form-notice');

    if (show && !notice) {
      const ctasBlock = document.querySelector('.cart__ctas');
      if (!ctasBlock) return;

      notice = document.createElement('p');
      notice.id = 'pharmacist-form-notice';
      notice.className = 'cart__pharmacist-form-notice';
      notice.setAttribute('role', 'status');
      notice.textContent = 'Please complete the pharmacist questionnaire before checking out.';
      ctasBlock.insertAdjacentElement('beforebegin', notice);
    }

    if (!show && notice) {
      notice.remove();
    }
  }

  onCartFormSubmit(event) {
    const rows = this.querySelectorAll('.cart-items__form-row');
    let allValid = true;

    rows.forEach((row) => {
      if (!this.validateRow(row, { markInvalid: true })) allValid = false;
    });

    if (!allValid) {
      event.preventDefault();

      const firstInvalid = this.querySelector(
        '.cart-items__field--invalid, .cart-items__question--invalid'
      );
      firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  async savePharmacistLine(row) {
    const fields = row.querySelectorAll('[data-property-name]');
    if (!fields.length) return;

    const lineIndex = row.dataset.lineIndex;
    if (lineIndex === undefined) return;

    const patientInfo = {};
    fields.forEach((field) => {
      const key = field.dataset.propertyName;
      if (field.type === 'radio') {
        if (field.checked) patientInfo[key] = field.value;
      } else if (field.type === 'checkbox') {
        patientInfo[key] = field.checked ? field.value : '';
      } else {
        patientInfo[key] = field.value;
      }
    });

    const body = JSON.stringify({
      line: Number(lineIndex) + 1,
      properties: {
        '_pharmacist-form': JSON.stringify(patientInfo),
      },
    });

    try {
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const parsedResponseText = await response.json();

      if (parsedResponseText.errors) {
        console.error('Pharmacist form save failed', parsedResponseText.errors);
        return;
      }

      this.dispatchEvent(
        new CustomEvent('cart:pharmacist-form-saved', {
          bubbles: true,
          detail: { row, cart: parsedResponseText },
        })
      );
    } catch (err) {
      console.error('Cart update request failed', err);
    }
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
